import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import usePageSEO from '../hooks/usePageSEO';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CMSPageView = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const lang = i18n.language?.substring(0, 2) || 'en';
  const pageTitle = page?.title?.[lang] || page?.title?.en || page?.slug || '';
  const seoTitle = lang === 'ar'
    ? (page?.meta_title_ar || pageTitle)
    : (page?.meta_title || pageTitle);
  const seoDescription = lang === 'ar'
    ? (page?.meta_description_ar || page?.meta_description || '')
    : (page?.meta_description || '');

  useEffect(() => {
    const load = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/pages/${slug}`);
        setPage(response.data);
      } catch {
        setPage(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  usePageSEO({
    title: seoTitle,
    description: seoDescription
  });

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen pt-24 pb-16 text-center py-20">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('cms.pageNotFound')}</h1>
        <Link to="/" className="text-red-600 hover:underline">{t('common.backToHome')}</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="cms-page">
      <div className="bg-gradient-to-br from-red-600 to-red-700 text-white py-12 mb-12">
        <div className="container mx-auto px-4">
          <Link to="/" className="inline-flex items-center text-red-100 hover:text-white mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />{t('common.backToHome')}
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold" data-testid="cms-page-title">{pageTitle}</h1>
        </div>
      </div>

      <div className="container mx-auto px-4">
        <div
          className="prose prose-lg max-w-4xl mx-auto prose-headings:text-gray-900 prose-p:text-gray-600 prose-li:text-gray-600 prose-a:text-red-600"
          dangerouslySetInnerHTML={{ __html: page.content[lang] || page.content.en }}
          data-testid="cms-page-content"
        />
      </div>
    </div>
  );
};

export default CMSPageView;
